import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/aplicativos-iptv/funplay")({
  head: () => ({
    meta: [
      { title: "Como Baixar e Instalar o Fun Play na Samsung, LG e Roku TV | P2 PRO" },
      { name: "description", content: "Aprenda como baixar e instalar o aplicativo Fun Play na sua Smart TV Samsung, LG ou Roku TV. Guia completo passo a passo atualizado em 2026." },
    ],
  }),
  component: () => (
    <SEOPage
      category="Tutorial"
      title="Como Baixar e Instalar o Fun Play na Samsung, LG e Roku TV"
      subtitle="Fun Play: Aplicativo Compatível com Painel IPTV Pós Pago"
      introParagraphs={[
        "O Fun Play é um aplicativo moderno desenvolvido para proporcionar uma experiênia prática e intuitiva em Smart TVs. Compatível com televisores Samsung, LG e Roku TV, o aplicativo oferece uma interface amigável, fácil configuração e excelente desempenho.",
        "Neste tutorial você aprenderá como baixar e instalar o Fun Play passo a passo em sua Smart TV."
      ]}
      sections={[
        {
          title: "O Que é o Fun Play?",
          type: "default",
          content: (
            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p>
                O Fun Play é um aplicativo utilizado para acesso a conteúdos em Smart TVs compatíveis. Sua interface foi desenvolvida para facilitar a navegação e proporcionar uma experiência confortável para toda a família.
              </p>
              <p>
                Além disso, o aplicativo é compatível com sistemas profissionais de Painel IPTV Pós Pago, amplamente utilizados por revendedores e provedores de streaming.
              </p>
            </div>
          )
        },
        {
          title: "Como Instalar o Fun Play na Samsung Smart TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Ligue sua Samsung Smart TV e conecte-a à internet." },
                  { step: 2, text: "Acesse a loja de aplicativos da Samsung." },
                  { step: 3, text: "Utilize a busca da loja." },
                  { step: 4, text: "Pesquise por: Fun Play" },
                  { step: 5, text: "Selecione o aplicativo oficial." },
                  { step: 6, text: "Clique em Instalar." },
                  { step: 7, text: "Após a instalação, abra o aplicativo." },
                  { step: 8, text: "Informe os dados de acesso fornecidos pelo seu revendedor." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Pronto! O Fun Play estará disponível em sua Samsung Smart TV.</p>
            </div>
          )
        },
        {
          title: "Como Instalar o Fun Play na LG Smart TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Conecte sua LG Smart TV à internet." },
                  { step: 2, text: "Abra a LG Content Store." },
                  { step: 3, text: "Utilize a pesquisa da loja." },
                  { step: 4, text: "Digite: Fun Play" },
                  { step: 5, text: "Selecione o aplicativo." },
                  { step: 6, text: "Clique em Instalar." },
                  { step: 7, text: "Aguarde a conclusão da instalação." },
                  { step: 8, text: "Abra o aplicativo e realize o login." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Agora o Fun Play está pronto para uso em sua TV LG.</p>
            </div>
          )
        },
        {
          title: "Como Instalar o Fun Play na Roku TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Acesse a tela inicial da Roku TV." },
                  { step: 2, text: "Entre na opção: Streaming Channels" },
                  { step: 3, text: "Utilize a busca da Roku Store." },
                  { step: 4, text: "Pesquise por: Fun Play" },
                  { step: 5, text: "Selecione o aplicativo oficial." },
                  { step: 6, text: "Clique em: Adicionar Canal" },
                  { step: 7, text: "Aguarde a instalação." },
                  { step: 8, text: "Abra o aplicativo e informe seus dados de acesso." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Pronto! Agora você pode utilizar o Fun Play em sua Roku TV.</p>
            </div>
          )
        },
        {
          title: "Dispositivos Compatíveis com o Fun Play",
          type: "featured",
          content: (
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Samsung Smart TV",
                "LG Smart TV",
                "Roku TV",
                "Dispositivos compatíveis com a loja oficial do aplicativo"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/5">
                  <CheckCircle2 className="w-5 h-5 text-electric shrink-0" />
                  <span className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">{item}</span>
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Principais Vantagens do Fun Play",
          type: "default",
          content: (
            <div className="grid sm:grid-cols-2 gap-4 text-muted-foreground">
              {[
                "Interface moderna",
                "Fácil instalação",
                "Navegação intuitiva",
                "Excelente desempenho",
                "Compatibilidade com Smart TVs",
                "Atualizações frequentes",
                "Integração com Painel IPTV Pós Pago"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Fun Play Compatível com Painel IPTV Pós Pago",
          type: "default",
          content: (
            <div className="space-y-6 text-muted-foreground leading-relaxed text-lg italic">
              <p>
                O Fun Play é uma excelente opção para usuários que procuram praticidade e compatibilidade com sistemas profissionais de Painel IPTV Pós Pago.
              </p>
              <p>
                Seu funcionamento integrado permite uma experiência mais estável e organizada, sendo uma das alternativas utilizadas por revendedores que buscam qualidade e facilidade de uso para seus clientes.
              </p>
            </div>
          )
        },
        {
          title: "Outros Aplicativos Compatíveis",
          type: "default",
          content: (
            <div className="space-y-4">
              <p className="text-muted-foreground">Além do Fun Play, nossa estrutura também oferece suporte para:</p>
              <ul className="grid gap-3 list-none p-0">
                {["One X", "P2PRO", "P2PLAY", "Prime Play"].map((item) => (
                  <li key={item} className="flex items-center gap-3 m-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground mt-4">Todos compatíveis com nosso sistema de Painel IPTV Pós Pago.</p>
            </div>
          )
        },
        {
          title: "Problemas para Encontrar o Fun Play?",
          type: "default",
          content: (
            <div className="grid gap-4">
              {[
                "Verifique a conexão com a internet.",
                "Atualize sua Smart TV.",
                "Reinicie o dispositivo.",
                "Atualize a loja de aplicativos.",
                "Entre em contato com o suporte."
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-red-500/5 border border-red-500/10">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-muted-foreground font-medium">{item}</span>
                </div>
              ))}
            </div>
          )
        }
      ]}
      benefits={[
        { title: "Interface moderna", description: "Design limpo para facilitar a navegação." },
        { title: "Fácil instalação", description: "Configurado em poucos minutos diretamente na loja." },
        { title: "Desempenho", description: "Streaming otimizado para evitar travamentos." }
      ]}
      howItWorks={[
        { title: "Download", description: "Acesse a loja da sua TV e procure por Fun Play." },
        { title: "Instalação", description: "Siga os passos do nosso guia ilustrado acima." },
        { title: "Acesso", description: "Use seus dados do Painel IPTV Pós Pago." }
      ]}
      diferenciais={[
        { title: "Nativo Smart TV", description: "Otimizado para Samsung, LG e Roku TV." },
        { title: "Pós Pago", description: "Integrado à nossa estrutura profissional." },
        { title: "Qualidade 4K", description: "Aproveite a máxima definição da sua TV." }
      ]}
      faq={[
        { question: "O Fun Play funciona na Samsung Smart TV?", answer: "Sim. O aplicativo possui compatibilidade com televisores Samsung compatíveis." },
        { question: "O Fun Play funciona na LG Smart TV?", answer: "Sim. O aplicativo está disponível para TVs LG compatíveis." },
        { question: "O Fun Play funciona na Roku TV?", answer: "Sim. O aplicativo pode ser instalado através da Roku Store." },
        { question: "Preciso de login para utilizar o Fun Play?", answer: "Sim. Após a instalação, basta inserir os dados de acesso fornecidos pelo seu revendedor." },
        { question: "O Fun Play é compatível com Painel IPTV Pós Pago?", answer: "Sim. O aplicativo é amplamente utilizado em sistemas profissionais de Painel IPTV Pós Pago." }
      ]}
      breadcrumbs={[
        { label: "Aplicativos", href: "/aplicativos-iptv" },
        { label: "Fun Play", href: "/aplicativos-iptv/funplay" }
      ]}
      ctaTitle="🚀 Quer Ter Seu Próprio Painel IPTV Pós Pago?"
      ctaDescription="Tenha acesso a uma estrutura profissional para revenda com aplicativos modernos e suporte especializado. One X, Fun Play, P2PRO, P2PLAY e Prime Play."
      ctaButtonText="QUERO TESTAR O PAINEL IPTV PÓS PAGO"
    />
  ),
});